import dayjs from 'dayjs'
import Styles from './CalendarEventCard.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Image } from './Image'
import { CSSProperties, useRef, useState } from 'react'
import cx from 'classnames'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { HoverOverlay } from './HoverOverlay/HoverOverlay'
import { Pressable, useHover } from '@react-aria/interactions'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { useProgress } from './useProgress'

import { StatusPill } from './StatusPill'
import { AlertTriangle } from 'blaze-ui/icons'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useEmailIntegrations } from 'hooks/useEmailIntegrations'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useStatus } from 'hooks/calendar/useStatus'
import { useValidationErrors } from 'hooks/calendar/useValidationErrors'
import { CalendarItemChannelLabel } from 'components/CalendarItemChannelLabel/CalendarItemChannelLabel'
import { getEventProperties } from './utils'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { useShowNextGenContentPreferences } from 'hooks/useShowNextGenContentPreferences'
import { STEP_ICONS } from 'components/AgenticStepIndicator/stepIcons'

export interface CalendarEventCardProps {
  event: AlmanacFileCalendarEvent
  cardHeight?: number
  cardWidth: number
  previewWidth?: number
  isSelectView?: boolean
  isSelected?: boolean
  onSelect?: (id: string) => void
  onEventClick?: (event: AlmanacFileCalendarEvent) => void
  loadingColor: string
  hasInnerPreview?: boolean
  innerPreviewV2?: boolean
  showStatus?: boolean
  maxDocPreviewLines?: number
  previewBorderRadius?: number
  canSelect?: boolean
  dateFormat?: string
  allowExpandCaption?: boolean
  showHoverOverlay?: boolean
  statusPillSize?: 'sm' | 'md'
  cardBorder?: string
  forceInnerPreviewWidth?: number | string
}

export const CalendarEventCard = ({
  event,
  cardWidth,
  cardHeight,
  previewWidth,
  isSelectView = false,
  isSelected = false,
  onSelect,
  onEventClick,
  loadingColor,
  hasInnerPreview = false,
  showStatus = true,
  maxDocPreviewLines,
  previewBorderRadius = 0,
  canSelect = true,
  dateFormat = 'h:mma',
  allowExpandCaption = true,
  showHoverOverlay = true,
  statusPillSize = 'md',
  cardBorder,
  innerPreviewV2,
  forceInnerPreviewWidth,
}: CalendarEventCardProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { connect } = useConnectAccountPopup()
  const workspace = useCurrentWorkspace()
  const { hoverProps, isHovered } = useHover({})
  const { start, channel, scheduled_post, created_at, image_url, empty, design_content } = event
  const [imageLoaded, setImageLoaded] = useState(false)
  const [designLoaded, setDesignLoaded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { connectedBlogIntegration } = useBlogIntegrations()
  const { connectedEmailIntegration } = useEmailIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()

  const { showNextGenContentPreferences: isGenV2 } = useShowNextGenContentPreferences()
  const isAIGeneratedVideo = !isGenV2 && event.is_ai_generated_video

  const { progress } = useProgress({ date: created_at })
  const progressData = event.progress
  const stepIcon = progressData?.current_step ? STEP_ICONS[progressData.current_step] : undefined

  const onConnect = (popUp?: Window) => {
    connect(popUp, {
      onConnect: () => {
        queryClient.invalidateQueries(calendarKeys.all)
        queryClient.invalidateQueries(['externalIntegrations', workspace?.id])
      },
    })
  }

  const { provider, validationErrors } = useValidationErrors({ event })
  const {
    didGenerationFail,
    hasUnconnectedTargets,
    isApproved,
    isDraft,
    isFailed,
    isHybrid,
    isNew,
    isPosted,
    isUpdating,
    isWarning,
    isPaused,
    needsApproval,
    needsConnect,
    targetAnalysis,
    unconnectedTargetCount,
  } = useStatus({
    event,
    validationErrors,
    workspace,
    connectedBlogIntegration,
    connectedEmailIntegration,
  })

  const isCopilot = !workspace?.is_virtual_marketer
  const { isArticlePreview, isLoading, isLoadingDocPreview, isWithinPreviewImageWaitTime, showDocText } =
    getEventProperties(event, imageLoaded, designLoaded, isCopilot)

  if (!event.channel) return null

  const isGeneratingWithContent = event.is_generating && !!event.image_url
  const canOpenPreview =
    (!event.scheduleContentId || event.key) && (!isLoading || !isWithinPreviewImageWaitTime || isGeneratingWithContent)

  const unconnectedProvider =
    hasUnconnectedTargets && !!event?.scheduled_post_targets?.length
      ? event.scheduled_post_targets?.find((target) => !target.external_integration_id)?.provider
      : provider

  const showBadge =
    showStatus &&
    !isLoading &&
    !didGenerationFail &&
    (isNew ||
      isPosted ||
      isFailed ||
      needsConnect ||
      isWarning ||
      isUpdating ||
      isDraft ||
      needsApproval ||
      isApproved ||
      isPaused)

  const classes =
    isLoading || !showStatus
      ? {}
      : {
          [Styles.failed]: isFailed,
          [Styles.posted]: isPosted,
          [Styles.warning]: isWarning,
          [Styles.new]: isNew,
          [Styles.canOpenPreview]: canOpenPreview,
        }

  const innerPreviewClasses = !hasInnerPreview
    ? {}
    : {
        [Styles.innerEventPreview]: !showDocText && (isArticlePreview || !!image_url || !!design_content || !!empty),
        [Styles.innerPreviewV2]: innerPreviewV2,
        [Styles.loadingInnerPreview]:
          isWithinPreviewImageWaitTime &&
          (isLoadingDocPreview || isLoading || (!image_url && !design_content && !showDocText && !empty)),
        [Styles.innerDocEventPreview]: showDocText,
      }

  const contentWidth = cardRef.current?.getBoundingClientRect().width || 0
  const innerPreviewWidth = previewWidth || cardWidth

  const forceCardWidthValue =
    typeof forceInnerPreviewWidth === 'string' ? forceInnerPreviewWidth : `${forceInnerPreviewWidth}px`

  return (
    <div
      key={event.key}
      {...hoverProps}
      ref={cardRef}
      className={cx(Styles.eventContainer, classes, {
        [Styles.hasInnerPreview]: hasInnerPreview,
        [Styles.innerPreviewV2]: innerPreviewV2,
      })}
      style={
        {
          '--card-width': `${forceInnerPreviewWidth ? forceCardWidthValue : cardWidth}px`,
          '--card-height': `${cardHeight}px`,
          '--inner-preview-width': `${innerPreviewWidth}px`,
          border: cardBorder,
        } as CSSProperties
      }
    >
      {canOpenPreview && showHoverOverlay && (
        <HoverOverlay
          event={event}
          isHovered={isHovered}
          isSelectView={isSelectView}
          isSelected={isSelected}
          onSelect={onSelect}
          onClick={() => onEventClick?.(event)}
          canSelect={canSelect}
        />
      )}
      <div
        className={cx(Styles.eventHeader, {
          [Styles.aiVideoHeader]: isAIGeneratedVideo,
          [Styles.innerPreviewV2]: innerPreviewV2,
        })}
      >
        <CalendarItemChannelLabel
          brandPlan={brandPlan}
          channel={channel ?? undefined}
          event={event}
          isAIGeneratedVideo={!!isAIGeneratedVideo}
          isHovered={isHovered}
        />
        <div className={cx(Styles.dateContainer, { [Styles.timeDateContainer]: dateFormat === 'MMM DD h:mma' })}>
          <Text variant="label">{start ? dayjs(start).format(dateFormat) : 'Not Scheduled'}</Text>
        </div>
      </div>
      <div className={cx(Styles.eventPreview, innerPreviewClasses)}>
        <div className={Styles.eventPreviewContent}>
          <LoadingShimmerer
            borderRadius={previewBorderRadius}
            show={isUpdating && !isLoading}
            isOverlay
            className={cx({ [Styles.innerPreviewLoadingShimmerer]: hasInnerPreview })}
          >
            <Image
              event={event}
              onImageLoaded={setImageLoaded}
              imageLoaded={imageLoaded}
              loadingColor={loadingColor}
              loadingProgress={progress}
              stepLabel={progressData?.step_label}
              stepIcon={stepIcon}
              referenceImageUrl={event.reference_image_url}
              containerRef={cardRef}
              cardWidth={previewWidth || contentWidth || cardWidth}
              previewWidth={previewWidth}
              designLoaded={designLoaded}
              onDesignLoaded={setDesignLoaded}
              maxDocPreviewLines={maxDocPreviewLines}
              allowExpandCaption={allowExpandCaption}
              didGenerationFail={didGenerationFail}
              isCopilot={isCopilot}
              generationStartedAt={created_at}
            />
          </LoadingShimmerer>
        </div>
      </div>
      {showBadge && (
        <>
          {isDraft && (
            <StatusPill
              testId="draft-badge"
              status="Draft"
              color={isSystemRedesignEnabled ? '#EAEDF1' : '#E5ECF3'}
              fontColor={isSystemRedesignEnabled ? 'rgba(52, 64, 84, 1)' : '#262B35'}
              size={statusPillSize}
            />
          )}
          {isWarning && (
            <Tooltip
              label={scheduled_post?.error_message || validationErrors.join('\n')}
              placement="top"
              hide={!(scheduled_post?.error_message || validationErrors.length > 0)}
            >
              <div className={Styles.statusCornerContainer}>
                <div className={Styles.warningPill} data-testid="warning-badge">
                  <AlertTriangle size={20} color="var(--dark-80)" />
                </div>
              </div>
            </Tooltip>
          )}
          {isNew && (
            <StatusPill testId="new-badge" status="New" color="#FCE6FE" fontColor="#A229FE" size={statusPillSize} />
          )}
          {isFailed && (
            <Tooltip
              label={scheduled_post?.error_message || validationErrors.join('\n')}
              placement="top"
              hide={!(scheduled_post?.error_message || validationErrors.length > 0)}
            >
              <div>
                <StatusPill
                  testId="failed-badge"
                  status="Failed"
                  color={isSystemRedesignEnabled ? '#FDECEC' : 'rgba(229, 53, 53, 1)'}
                  fontColor={isSystemRedesignEnabled ? 'var(--red-70)' : 'var(--light-100)'}
                  size={statusPillSize}
                />
              </div>
            </Tooltip>
          )}
          {isPosted && (
            <StatusPill
              testId="posted-badge"
              status="Posted"
              color={isSystemRedesignEnabled ? '#ECFDF3' : 'rgba(214, 255, 230, 1)'}
              fontColor={isSystemRedesignEnabled ? 'rgba(7, 148, 42, 1)' : 'rgba(0, 81, 8, 1)'}
              size={statusPillSize}
            />
          )}
          {isHybrid && !targetAnalysis.useOldLogic && (
            <Tooltip
              label={`${targetAnalysis.successfulTargets.length} successful, ${targetAnalysis.failedTargets.length} failed`}
              placement="top"
            >
              <div className={Styles.hybridAlertContainer}>
                <div className={Styles.hybridAlert} data-testid="hybrid-warning-badge">
                  <AlertTriangle
                    size={16}
                    color={isSystemRedesignEnabled ? 'var(--light-100)' : 'var(--negative-60)'}
                  />
                </div>
              </div>
            </Tooltip>
          )}
          {needsConnect && unconnectedProvider && (
            <StatusPill
              testId="connect-badge"
              status={
                <InitiateOauthTrigger popUp provider={unconnectedProvider} onInitiatedOauth={onConnect}>
                  <Pressable>
                    <span aria-label={`Connect ${unconnectedProvider}`}>
                      {unconnectedTargetCount > 0
                        ? `${unconnectedTargetCount} ${
                            unconnectedTargetCount === 1 ? 'Account' : 'Accounts'
                          } to Connect`
                        : 'Connect'}
                    </span>
                  </Pressable>
                </InitiateOauthTrigger>
              }
              color={isSystemRedesignEnabled ? '#FFF9EF' : 'rgba(113, 126, 151, 1)'}
              fontColor={isSystemRedesignEnabled ? 'var(--yellow-70)' : 'var(--light-100)'}
              size={statusPillSize}
            />
          )}
          {isUpdating && (
            <StatusPill
              testId="updating-badge"
              status="Updating"
              color="rgba(177, 190, 205, 1)"
              fontColor="var(--dark-80)"
              size={statusPillSize}
            />
          )}
          {needsApproval && (
            <StatusPill
              testId="review-badge"
              status="Review"
              color="#F8F0FF"
              fontColor="#A229FE"
              size={statusPillSize}
            />
          )}
          {isApproved && !needsConnect && (
            <StatusPill
              testId="approved-badge"
              status="Approved"
              color="#EFF7FF"
              fontColor="#157EEF"
              size={statusPillSize}
            />
          )}
          {isPaused && (
            <StatusPill
              testId="paused-badge"
              status="Paused"
              color="#EAEDF1"
              fontColor="#344054"
              size={statusPillSize}
            />
          )}
        </>
      )}
    </div>
  )
}
